import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient, useMe } from '@fms/api-client';
import type { AuthUserDto } from '@fms/contracts';

interface SessionValue {
  user: AuthUserDto | null;
  ready: boolean;
  /** Awaitable so login can confirm the session before navigating. */
  refetch: () => Promise<unknown>;
}

const SessionContext = createContext<SessionValue>({
  user: null,
  ready: false,
  refetch: () => Promise.resolve(),
});

/**
 * Attempts a silent session restore (refresh cookie) once on boot, then keeps
 * the current user available app-wide. `ready` gates the first render so
 * guards don't flash the login screen while the refresh is in flight.
 *
 * While signed in, a Socket.IO connection to the /citizen namespace listens
 * for `application:status` pushed by the worker and invalidates the affected
 * queries — status changes appear without a manual refresh.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [bootstrapped, setBootstrapped] = useState(false);
  // Reactive mirror of the in-memory access token: login/refresh/logout all
  // reflect here instantly, so signed-in UI flips without a reload.
  const [token, setToken] = useState<string | null>(() => client.getAccessToken());

  useEffect(() => client.subscribeToken(setToken), [client]);

  useEffect(() => {
    let active = true;
    void client.bootstrap().finally(() => {
      if (active) setBootstrapped(true);
    });
    return () => {
      active = false;
    };
  }, [client]);

  const me = useMe(bootstrapped && Boolean(token));
  const ready = bootstrapped && !me.isLoading;
  // Sessions are per-portal, but keep the belt-and-braces type check: only a
  // CITIZEN account is ever "signed in" here (the API enforces the same split
  // with requireCitizen()).
  const user = token && me.data?.user.type === 'CITIZEN' ? me.data.user : null;
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    const origin = new URL(client.axios.defaults.baseURL ?? window.location.origin).origin;
    const socket: Socket = io(`${origin}/citizen`, {
      transports: ['websocket'],
      // Fresh token on every (re)connect — the server validates at handshake.
      auth: (cb) => cb({ token: client.getAccessToken() }),
    });
    socket.on('application:status', (event: { applicationId?: string }) => {
      void queryClient.invalidateQueries({ queryKey: ['applications', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['drafts', 'mine'] });
      if (event?.applicationId) {
        void queryClient.invalidateQueries({ queryKey: ['application', event.applicationId] });
        void queryClient.invalidateQueries({ queryKey: ['timeline', event.applicationId] });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [userId, client, queryClient]);

  return (
    <SessionContext.Provider value={{ user, ready, refetch: me.refetch }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  return useContext(SessionContext);
}
